import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDate } from '../lib/dateUtils';

export const RatioAnalysisView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const fromDate = useStore((state) => state.fromDate);
  const toDate = useStore((state) => state.toDate);

  const ratioData = useLiveQuery(async () => {
    if (!activeCompany) return null;

    // 1. Get Sales
    const salesGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Sales Accounts').first();
    const salesLedgers = await db.ledgers.where('groupId').equals(salesGroup?.id || -1).toArray();
    let totalSales = 0;
    
    let includeOpening = false;
    if (activeCompany.financialYearStart && fromDate <= activeCompany.financialYearStart) {
      includeOpening = true;
    }

    for (const l of salesLedgers) {
      if (includeOpening) {
         totalSales += l.balanceType === 'Cr' ? l.openingBalance : -l.openingBalance;
      }
      const entries = await db.voucherEntries.where('ledgerId').equals(l.id!).toArray();
      for (const e of entries) {
        const v = await db.vouchers.get(e.voucherId);
        if (v && !v.deleted && v.date >= fromDate && v.date <= toDate) {
          totalSales += (e.type === 'Cr' ? e.amount : -e.amount);
        }
      }
    }

    // 2. Get Expenses
    const expenseGroups = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.nature === 'Expenses').toArray();
    let totalExpenses = 0;
    for (const g of expenseGroups) {
      const ledgers = await db.ledgers.where('groupId').equals(g.id!).toArray();
      for (const l of ledgers) {
        if (includeOpening) {
           totalExpenses += l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance;
        }
        const entries = await db.voucherEntries.where('ledgerId').equals(l.id!).toArray();
        for (const e of entries) {
          const v = await db.vouchers.get(e.voucherId);
          if (v && !v.deleted && v.date >= fromDate && v.date <= toDate) {
            totalExpenses += (e.type === 'Dr' ? e.amount : -e.amount);
          }
        }
      }
    }

    // 3. Get Profit (Simplified: Sales - Expenses)
    const profit = totalSales - totalExpenses;

    // 4. Current Assets & Liabilities
    const caGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Current Assets').first();
    const clGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Current Liabilities').first();
    
    const getGroupBalance = async (groupId: number, visited = new Set<number>()) => {
      let bal = 0;
      if (visited.has(groupId)) return 0;
      visited.add(groupId);

      const ledgers = await db.ledgers.where('groupId').equals(groupId).toArray();
      for (const l of ledgers) {
        let lBal = l.balanceType === 'Dr' ? l.openingBalance : -l.openingBalance;
        const entries = await db.voucherEntries.where('ledgerId').equals(l.id!).toArray();
        for (const e of entries) {
          const v = await db.vouchers.get(e.voucherId);
          if (v && !v.deleted && v.date <= toDate) {
            lBal += (e.type === 'Dr' ? e.amount : -e.amount);
          }
        }
        bal += lBal;
      }
      // Also check sub-groups
      const subGroups = await db.groups.where('parentGroupId').equals(groupId).toArray();
      for (const sg of subGroups) {
        bal += await getGroupBalance(sg.id!, visited);
      }
      return bal;
    };

    const currentAssets = await getGroupBalance(caGroup?.id || -1);
    const currentLiabilities = Math.abs(await getGroupBalance(clGroup?.id || -1));

    // Inventory (Stock-in-hand)
    const stockGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Stock-in-hand').first();
    const stockInHand = await getGroupBalance(stockGroup?.id || -1);
    
    // Capital
    const capitalGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Capital Account').first();
    const equity = Math.abs(await getGroupBalance(capitalGroup?.id || -1));
    
    // Debt (Loans (Liability))
    const loansGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Loans (Liability)').first();
    const debt = Math.abs(await getGroupBalance(loansGroup?.id || -1));

    // Ratios
    const profitMargin = totalSales > 0 ? (profit / totalSales) * 100 : 0;
    const expenseRatio = totalSales > 0 ? (totalExpenses / totalSales) * 100 : 0;
    const currentRatio = currentLiabilities > 0 ? currentAssets / currentLiabilities : 0;
    const quickRatio = currentLiabilities > 0 ? (currentAssets - stockInHand) / currentLiabilities : 0;
    const debtEquityRatio = equity > 0 ? debt / equity : 0;

    const totalAssets = currentAssets + stockInHand;
    const workingCapital = currentAssets - currentLiabilities;
    const roa = totalAssets > 0 ? (profit / totalAssets) * 100 : 0;
    const workingCapitalTurnover = workingCapital > 0 ? totalSales / workingCapital : 0;
    const roe = equity > 0 ? (profit / equity) * 100 : 0;

    return {
      totalSales,
      totalExpenses,
      profit,
      currentAssets,
      currentLiabilities,
      stockInHand,
      equity,
      debt,
      ratios: [
        { name: 'Profit Margin', value: profitMargin.toFixed(2) + '%', desc: 'Net Profit / Total Sales' },
        { name: 'Expense Ratio', value: expenseRatio.toFixed(2) + '%', desc: 'Total Expenses / Total Sales' },
        { name: 'Current Ratio', value: currentRatio.toFixed(2), desc: 'Current Assets / Current Liabilities' },
        { name: 'Quick Ratio', value: quickRatio.toFixed(2), desc: '(Current Assets - Inventory) / Current Liab' },
        { name: 'Return on Assets', value: roa.toFixed(2) + '%', desc: 'Net Profit / Total Assets' },
        { name: 'Return on Equity', value: roe.toFixed(2) + '%', desc: 'Net Profit / Shareholder Equity' },
        { name: 'Debt/Equity Ratio', value: debtEquityRatio.toFixed(2), desc: 'Total Debt / Total Equity' },
        { name: 'Working Capital Turnover', value: workingCapitalTurnover.toFixed(2), desc: 'Sales / Working Capital' },
      ]
    };
  }, [activeCompany, fromDate, toDate]);

  if (!ratioData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Calculating Ratios...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px]">
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Ratio Analysis</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
        <span className="text-[#1e8585]">Performance Indicators</span>
        <span>{formatDate(fromDate)} to {formatDate(toDate)}</span>
      </div>

      <div className="flex-1 p-8 overflow-y-auto bg-[#f8fafc]">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Summary Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col gap-4">
            <h3 className="text-[16px] font-bold text-[#1e8585] border-b pb-2">Business Summary</h3>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Sales:</span>
              <span className="font-bold">₹{ratioData?.totalSales.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Expenses:</span>
              <span className="font-bold">₹{ratioData?.totalExpenses.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-gray-600 font-semibold">Net Profit:</span>
              <span className={`font-bold ${(ratioData?.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                ₹{ratioData?.profit.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Ratios Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col gap-4">
            <h3 className="text-[16px] font-bold text-[#1e8585] border-b pb-2">Key Ratios</h3>
            {ratioData?.ratios.map((ratio, i) => (
              <div key={i} className="flex flex-col border-b border-gray-50 pb-2 last:border-0">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">{ratio.name}</span>
                  <span className="text-[18px] font-bold text-[#1e8585]">{ratio.value}</span>
                </div>
                <span className="text-[10px] text-gray-400 italic">{ratio.desc}</span>
              </div>
            ))}
          </div>

          {/* Liquidity Card */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col gap-4 md:col-span-2">
            <h3 className="text-[16px] font-bold text-[#1e8585] border-b pb-2">Liquidity Position</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col items-center p-4 bg-emerald-50 rounded">
                <span className="text-emerald-700 font-semibold">Current Assets</span>
                <span className="text-[16px] font-bold">₹{ratioData?.currentAssets.toFixed(2)}</span>
              </div>
              <div className="flex flex-col items-center p-4 bg-red-50 rounded">
                <span className="text-red-700 font-semibold">Current Liabilities</span>
                <span className="text-[16px] font-bold">₹{ratioData?.currentLiabilities.toFixed(2)}</span>
              </div>
              <div className="flex flex-col items-center p-4 bg-blue-50 rounded">
                <span className="text-blue-700 font-semibold">Working Capital</span>
                <span className="text-[16px] font-bold">₹{((ratioData?.currentAssets || 0) - (ratioData?.currentLiabilities || 0)).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner mt-auto">
        <div className="flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100" onClick={() => handleItemSelect({ name: 'Quit' })}>
          <div className="flex items-center space-x-[2px] whitespace-nowrap">
            <span className="text-[#2a62a3] font-bold underline decoration-[#2a62a3] underline-offset-2">Q</span>
            <span className="text-[#2a62a3] font-bold">:</span><span className="text-black leading-none">Quit</span>
          </div>
        </div>
      </div>
    </div>
  );
};
